import React from 'react';
import _memoize from 'lodash/memoize';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { ValidationError } from 'yup';
import { FieldChangeCallBack, FieldItem, Value } from '@lib/interfaces/form';
import { Steps } from '@lib/enums/form';
import FieldArray from '@lib/components/ReactHookForm/FieldArray';
import FormItem from '@lib/components/ReactHookForm/FormItem';
import GroupFieldWrapper from '@lib/components/ReactHookForm/GroupFieldWrapper';
import { SourceAppendValues } from '@lib/components/ReactHookForm/types';
import styles from './Form.module.scss';

type FormData = {
  fieldsArray: React.ReactNode[];
  schemaObj: Record<string, Yup.AnySchema<any, any, any>>;
  fieldChangeCallBacks: FieldChangeCallBack[];
  uniqueSubFieldNames?: string[];
};

function isArrayFieldOptionalAndEmpty(value: Value) {
  return Array.isArray(value) || value === null || value === '';
}

// Helper function to add callback only if it's not already in the array
function addUniqueCallback(
  callbacks: FieldChangeCallBack[],
  newCallback?: FieldChangeCallBack,
): void {
  if (newCallback) {
    const alreadyExists = callbacks.includes(newCallback);
    if (!alreadyExists) {
      callbacks.push(newCallback);
    } else {
      console.warn('Attempted to add duplicate callback:', newCallback);
    }
  }
}

// Yup unique method checks array filed values. Empty values are ignored as optional.
Yup.addMethod(Yup.array, 'unique', function unique(field, message) {
  return this.test('unique', message, function uniqueFn(array = []) {
    const { path, createError } = this;
    const seenValues = new Map();
    const errors: ValidationError[] = [];

    array.forEach((item, index) => {
      const value = item?.[field]?.toLowerCase();

      if (!value || isArrayFieldOptionalAndEmpty(item[field])) {
        return;
      }

      if (seenValues.has(value)) {
        errors.push(
          createError({
            path: `${path}[${index}].${field}`,
            message,
          }),
        );
        errors.push(
          createError({
            path: `${path}[${seenValues.get(value)}].${field}`,
            message,
          }),
        );
      } else {
        seenValues.set(value, index);
      }
    });

    return errors.length > 0 ? new Yup.ValidationError(errors) : true;
  });
});

type GetFieldArgs = {
  field: FieldItem;
  formId?: string;
  formItemsClassName?: string;
  isVisible?: boolean;
  sourceAppendValues?: SourceAppendValues;
  stepIndex?: Steps;
  t: TFunction<'translation', undefined>;
};

function processField(args: GetFieldArgs) {
  const {
    field,
    formId,
    formItemsClassName,
    isVisible = true,
    sourceAppendValues,
    stepIndex,
    t,
  } = args;
  const formData: FormData = {
    fieldsArray: [],
    schemaObj: {},
    fieldChangeCallBacks: [],
  };

  const {
    fieldComponents,
    isDraggable,
    isSubItemDraggableFn,
    isSubItemRemovableFn,
    name,
    subFieldEmptyStateText,
    subFieldEmptyStateTitle,
    subFieldTitle,
    subFields,
    subFieldsAddMoreButtonText,
    subFieldsEntity,
    subFieldsMaxLength,
    subFieldsMaxLengthTooltipProps,
    subFieldsMinLength,
    validation,
    ...restFieldProps
  } = field;

  if (subFields && subFields.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
    const subFormData = processSubFields({
      formId,
      formItemsClassName,
      isVisible,
      sourceAppendValues,
      stepIndex,
      subFields,
      t,
    });
    formData.fieldsArray.push(
      <FieldArray
        key={name}
        parentName={name}
        {...restFieldProps}
        emptyStateText={subFieldEmptyStateText}
        emptyStateTitle={subFieldEmptyStateTitle}
        entity={subFieldsEntity}
        fieldComponents={fieldComponents}
        formId={formId}
        formItemsClassName={formItemsClassName}
        isDraggable={isDraggable}
        isSubItemDraggableFn={isSubItemDraggableFn}
        isSubItemRemovableFn={isSubItemRemovableFn}
        isVisible={isVisible}
        sourceAppendValues={sourceAppendValues}
        subFields={subFields}
        subFieldsAddMoreButtonText={subFieldsAddMoreButtonText}
        subFieldsMaxLength={subFieldsMaxLength}
        subFieldsMaxLengthTooltipProps={subFieldsMaxLengthTooltipProps}
        subFieldsMinLength={subFieldsMinLength}
        title={subFieldTitle}
      />,
    );
    // Start with default array validation for subFields
    let fieldValidation = Yup.array().of(
      Yup.object().shape(subFormData.schemaObj),
    );

    // If custom validation is provided, merge it with subFields validation
    if (validation) {
      try {
        // Check if validation is array type by trying to access array-specific methods
        const validationAsAny = validation as any;

        if (
          // eslint-disable-next-line no-underscore-dangle
          validationAsAny._type === 'array' ||
          typeof validationAsAny.of === 'function'
        ) {
          // Custom validation is array type, preserve it and merge with subFields schema
          const subFieldsSchema = Yup.object().shape(subFormData.schemaObj);

          // Check if the custom validation already has an .of() method defined
          // (indicating it already includes item validation)
          if (validationAsAny.innerType) {
            // Custom validation already has item validation, use it as-is
            fieldValidation = validation as any;
          } else {
            // Custom validation doesn't have item validation, add it
            fieldValidation = (validation as any).of(subFieldsSchema);
          }
        } else {
          // Custom validation is not array type, log warning and use default
          console.warn(
            // eslint-disable-next-line no-underscore-dangle
            `Field "${name}" has subFields but validation is not array type (${validationAsAny._type}). Using default array validation with subFields schema.`,
          );
          fieldValidation = Yup.array().of(
            Yup.object().shape(subFormData.schemaObj),
          );
        }
      } catch (error) {
        // If we can't determine validation type, use default and log warning
        console.warn(
          `Field "${name}" has subFields but could not merge custom validation. Using default array validation.`,
          error,
        );
        fieldValidation = Yup.array().of(
          Yup.object().shape(subFormData.schemaObj),
        );
      }
    }

    // TODO: finish it
    // if (subFieldsMinLength) {
    //   fieldValidation = fieldValidation.min(subFieldsMinLength, t('min-length-error'));
    // }
    // if (subFieldsMaxLength) {
    //   fieldValidation = fieldValidation.max(subFieldsMaxLength, t('max-length-error'));
    // }
    if (subFormData.uniqueSubFieldNames?.length) {
      subFormData.uniqueSubFieldNames.forEach((uniqueSubFieldName) => {
        // @ts-ignore
        fieldValidation = fieldValidation.unique(
          uniqueSubFieldName,
          t('unique-field-error'),
        );
      });
    }
    formData.schemaObj[name] = fieldValidation;
    subFormData.fieldChangeCallBacks.forEach((callback) => {
      addUniqueCallback(formData.fieldChangeCallBacks, callback);
    });

    addUniqueCallback(
      formData.fieldChangeCallBacks,
      restFieldProps.fieldChangeCallBack,
    );
  } else {
    formData.fieldsArray.push(
      <FormItem
        {...restFieldProps}
        name={name}
        key={name}
        formId={formId}
        validation={validation}
        isVisible={isVisible}
      />,
    );
    if (validation) {
      formData.schemaObj[name] = validation;
    }
    addUniqueCallback(
      formData.fieldChangeCallBacks,
      restFieldProps.fieldChangeCallBack,
    );
  }

  return formData;
}

type SubFields = {
  formId?: string;
  formItemsClassName?: string;
  isVisible?: boolean;
  sourceAppendValues?: SourceAppendValues;
  stepIndex?: Steps;
  subFields: FieldItem[];
  t: TFunction<'translation', undefined>;
};

function processSubFields(args: SubFields) {
  const {
    formId,
    formItemsClassName,
    isVisible,
    sourceAppendValues,
    stepIndex,
    subFields,
    t,
  } = args;
  const formData: FormData = {
    fieldsArray: [],
    schemaObj: {},
    fieldChangeCallBacks: [],
    uniqueSubFieldNames: [],
  };
  subFields.forEach((field) => {
    const { fieldsArray, schemaObj, fieldChangeCallBacks } = processField({
      field,
      formId,
      formItemsClassName,
      isVisible,
      sourceAppendValues,
      stepIndex,
      t,
    });
    formData.fieldsArray.push(...fieldsArray);
    formData.schemaObj = { ...formData.schemaObj, ...schemaObj };
    fieldChangeCallBacks.forEach((callback) => {
      addUniqueCallback(formData.fieldChangeCallBacks, callback);
    });
    if (field.isUnique && formData.uniqueSubFieldNames) {
      formData.uniqueSubFieldNames.push(field.name);
    }
  });

  return formData;
}

type GetFieldsArgs = {
  fields: FieldItem[];
  formId?: string;
  formItemsClassName?: string;
  stepIndex?: Steps;
  t: TFunction<'translation', undefined>;
  sourceAppendValues?: SourceAppendValues;
};

function getFormData(args: GetFieldsArgs) {
  const {
    fields,
    formId,
    formItemsClassName,
    stepIndex,
    sourceAppendValues,
    t,
  } = args;
  const formData: FormData = {
    fieldsArray: [],
    schemaObj: {},
    fieldChangeCallBacks: [],
  };

  fields.forEach((field) => {
    const {
      name,
      groupFields,
      element: Element,
      componentProps,
      isVisibleFn,
      step = Steps.step1,
    } = field;
    const isVisible = stepIndex === step && field.isVisible !== false;
    const isGroupField = groupFields?.length;
    if (isGroupField) {
      // Render field group
      const group: React.ReactNode[] = [];
      groupFields.forEach((groupField) => {
        const { fieldsArray, schemaObj, fieldChangeCallBacks } = processField({
          field: groupField,
          formId,
          formItemsClassName,
          isVisible,
          sourceAppendValues,
          stepIndex,
          t,
        });
        group.push(...fieldsArray);
        formData.schemaObj = { ...formData.schemaObj, ...schemaObj };
        fieldChangeCallBacks.forEach((callback) => {
          addUniqueCallback(formData.fieldChangeCallBacks, callback);
        });
      });
      if (group.length) {
        formData.fieldsArray.push(
          <GroupFieldWrapper
            key={name}
            name={name}
            element={Element}
            componentProps={componentProps}
            formItemsClassName={formItemsClassName}
            isVisibleFn={isVisibleFn}
            isVisible={isVisible}
          >
            {group}
          </GroupFieldWrapper>,
        );
      }
    } else {
      const { fieldsArray, schemaObj, fieldChangeCallBacks } = processField({
        field,
        formId,
        formItemsClassName,
        sourceAppendValues,
        stepIndex,
        t,
        isVisible,
      });
      formData.fieldsArray.push(...fieldsArray);
      formData.schemaObj = { ...formData.schemaObj, ...schemaObj };
      fieldChangeCallBacks.forEach((callback) => {
        addUniqueCallback(formData.fieldChangeCallBacks, callback);
      });
    }
  });

  return formData;
}

export default _memoize(getFormData);
